// Feature rollout (Phase: admin/role-based-access §3c). See Phases/admin/PHASE-ROLE-BASED-ACCESS.md.
//
// WHY THIS IS SEPARATE FROM `access`. "Who is entitled to this?" and "has this shipped yet?" are
// different questions and need different switches. Overloading one permission key with both is
// exactly why nobody could tell, from the code, which of the nine staff-hidden PE pages were
// permanent operator tools and which were simply unfinished.
//
//   access  (auth/tiers.ts)  → WHO. A tier or capability. Permanent.
//   feature (this file)      → WHETHER. A rollout stage. Temporary, by design.
//
// A page declares both. Launching is one value change here, and the entitlement is already
// declared correctly on the route — no archaeology needed at launch time.
import type { Tier } from '@/auth/tiers';

/** Features currently mid-rollout. Entries are DELETED once a feature is fully launched. */
export type FeatureKey =
  | 'pe.exitWatch'
  | 'pe.ask'
  | 'pe.marketMap'
  // Element-level, not a whole page: the Appetite column + its filter on /pe/firms.
  | 'pe.firmsAppetite'
  // The organization CRM page.
  | 'org.crm'
  /** Organization domains tab — not ready for tenants or org admins. */
  | 'org.domains'
  // Research-rail pages that are not ready for customers yet.
  | 'research.knowledgeBank'
  | 'research.emailSync'
  /** Result workspace Summary tab — not ready for customers. */
  | 'research.summary';

/**
 * How far a feature has rolled out.
 *
 * `staff` — visible to platform staff only. The page and its nav link are invisible to everyone
 *           else (404, not 403: an unreleased feature does not exist as far as a customer is
 *           concerned).
 * `all`   — rolled out. The route's own `access` is the only gate; this flag stops mattering and
 *           the entry should be deleted from `FEATURE_ROLLOUT` along with the `feature` on the
 *           route and nav item.
 */
export type RolloutStage = 'staff' | 'all';

/**
 * The rollout board. **To launch a feature to customers, change its value to `'all'`** — the route
 * gate, the nav link and the denial mode all follow, because each already declares the entitlement
 * its audience will need (`access: 'pe:dataset'` for all three below).
 *
 * Operator decision 2026-09-10: Exit Watch, Ask the Market and Market Map are customer features
 * (`pe:dataset`) held at staff-only until they are ready. Digest, Activity, Analysis, Corrections,
 * Action Queue and PE Admin are permanent staff tools and therefore carry NO feature key — they are
 * gated by `access: 'pe:admin'` alone and are not waiting on anything.
 *
 * A feature key is NOT only for whole pages. `pe.firmsAppetite` gates two elements inside a page
 * that stays open — the mechanism is the same, the granularity is the caller's choice.
 *
 * **The backend mirrors this board.** `app/dependencies.py::UNLAUNCHED_PE_FEATURES` in
 * Business-Research-Tool---Quralyst staff-gates the matching endpoints, so launching a feature is
 * one edit HERE **and** one there. They must move together: flipping only this ships a page whose
 * API 403s; flipping only the backend opens an endpoint for a page nobody can reach.
 *
 * Several keys have no backend counterpart, on purpose:
 *  • `pe.exitWatch` — its endpoint (/pe/signals/exit-readiness) is ALREADY customer-facing: the
 *    Holdings table calls it for every pe:dataset user. Staff-gating it would break a shipped page
 *    to hide data customers already receive, so this one is a UI rollout only.
 *  • `pe.firmsAppetite` / `org.crm` — element/page visibility over endpoints customers already use.
 *  • `research.knowledgeBank` / `research.emailSync` — the same: pages that HAVE shipped already
 *    call both APIs (KnowledgeBankNote, EmailNudgeBanner, usePriorContacts), so gating the
 *    endpoints would break them. Holding back the page is the whole change.
 */
export const FEATURE_ROLLOUT: Record<FeatureKey, RolloutStage> = {
  'pe.exitWatch': 'staff',
  'pe.ask': 'staff',
  'pe.marketMap': 'staff',
  // Operator decision 2026-09-10 (second pass). Not whole pages:
  //  • pe.firmsAppetite — the Appetite column and its filter dropdown on /pe/firms. The page stays
  //    open to pe:dataset; only these two elements are held back.
  //  • org.crm / org.domains — org tabs kept in orgSections + the sidebar, held at staff until
  //    they are ready for tenants and org admins. Platform staff still see the tabs. To launch,
  //    flip the value to 'all' here — do not delete the nav items.
  'pe.firmsAppetite': 'staff',
  'org.crm': 'staff',
  'org.domains': 'staff',
  // Operator decision 2026-09-15. Two Research-rail pages held back until they are ready. Both are
  // UI rollouts only (see the backend note above):
  //  • research.knowledgeBank — the /knowledge-bank management page. What a run actually USED is
  //    still reported on every result page by KnowledgeBankNote; only managing entries is hidden.
  //  • research.emailSync — the /email-sync settings page. EmailNudgeBanner links straight at it
  //    from the result pages, so that banner carries the same flag or it would offer a 404.
  'research.knowledgeBank': 'staff',
  'research.emailSync': 'staff',
  // Result Summary tab and page. Not ready for customers — platform staff only. Flip to 'all'
  // to launch. UI rollout only: the summary endpoint stays available to the result owner.
  'research.summary': 'staff',
};

/** The tier a stage requires. `all` requires nothing beyond the route's own `access`. */
export function stageRequiresTier(stage: RolloutStage): Tier | null {
  return stage === 'staff' ? 'platform' : null;
}

/**
 * Whether the rollout stage lets `tier` see the feature. Entitlement is checked separately, by
 * `access` — this answers only "has it shipped to this audience yet".
 */
export function featureRolledOutTo(feature: FeatureKey, tier: Tier): boolean {
  const stage = FEATURE_ROLLOUT[feature];
  // An unknown key must not silently open a gate: treat it as unreleased.
  if (!stage) return false;
  return stage === 'all' || tier === 'platform';
}
